import cn from "classnames";
import { dateTimeToView, dateToView } from "helpers/date";
import {
    shippingDocumentProviderTypes,
    shippingOrderOperationTypes,
    shippingOrderStatuses,
    shippingOrderStatusesEnum,
    shippingOrderTitleStatuses,
    shippingOrderTitleStatusesEnum,
    shippingOrderTitleTypes,
    shippingOrderTitleTypesEnum,
    shippingOrderUnmatchingReasons,
} from "helpers/enums";
import { isShippingOrderDeclined } from "helpers/shippingOrder";
import { enumLabel, formatNumber, hasValue } from "helpers/system";
import React from "react";

const statusClassName = (status) =>
    cn("badge", {
        "badge-secondary": status === shippingOrderStatusesEnum.Received,
        "badge-info": status === shippingOrderStatusesEnum.DistributionBidCreated,
        "badge-primary": status === shippingOrderStatusesEnum.ResponseTitleGenerated,
        "badge-success": [
            shippingOrderStatusesEnum.ResponseTitleSent,
            shippingOrderStatusesEnum.Completed,
            shippingOrderStatusesEnum.ManuallyDistributionBidCreated,
            shippingOrderStatusesEnum.ManuallyDistributionBidAttached,
        ].includes(status),
        "badge-warning": status === shippingOrderStatusesEnum.ManuallyDistributionBidDetached,
        "badge-danger": [
            shippingOrderStatusesEnum.FailedToCreateDistributionBid,
            shippingOrderStatusesEnum.FailedToGenerateResponseTitle,
            shippingOrderStatusesEnum.FailedToSendResponseTitle,
        ].includes(status),
    });

const titleStatusClassName = (status) =>
    cn("badge", {
        "badge-secondary": status === shippingOrderTitleStatusesEnum.ReceivedOrCreated,
        "badge-success": status === shippingOrderTitleStatusesEnum.Signed,
    });

export const ShippingOrderStatusBadge = ({ status, className = "" }) => {
    const label = enumLabel(shippingOrderStatuses, status);
    return (
        <span className={cn(statusClassName(status), className)} title={label}>
            {label}
        </span>
    );
};

export const ShippingOrderDeclineBadge = ({ shippingOrder }) =>
    isShippingOrderDeclined(shippingOrder) ? (
        <span className="badge badge-danger ml-1 flex-shrink-0">Отказ</span>
    ) : null;

const InfoRow = ({ label, children }) => (
    <>
        <div className="show-bid__main-info--title">{label}</div>
        <div className="show-bid__main-info--value">{children}</div>
    </>
);

const ParticipantInfo = ({ title, participant }) => {
    if (!participant) {
        return null;
    }
    const requisites = [participant.inn && `ИНН ${participant.inn}`, participant.kpp && `КПП ${participant.kpp}`]
        .filter(Boolean)
        .join(", ");
    return (
        <div className="b-etrn__card p-0">
            <div className="show-bid__main-info">
                <div className="font-weight-bold mb-2">{title}</div>
                <div />
                {hasValue(participant.fullName) && <InfoRow label="Наименование">{participant.fullName}</InfoRow>}
                {requisites && <InfoRow label="Реквизиты">{requisites}</InfoRow>}
                {hasValue(participant.ogrnip) && <InfoRow label="ОГРНИП">{participant.ogrnip}</InfoRow>}
                {hasValue(participant.address) && <InfoRow label="Адрес">{participant.address}</InfoRow>}
                {hasValue(participant.phoneNumber) && <InfoRow label="Телефон">{participant.phoneNumber}</InfoRow>}
                {hasValue(participant.email) && <InfoRow label="Электронная почта">{participant.email}</InfoRow>}
            </div>
        </div>
    );
};

const FileLink = ({ file }) => {
    if (!file?.accessUrlLink) {
        return null;
    }

    const fileName = file.originalFileName || file.fileName || `Файл ${file.id}`;

    return (
        <div>
            <a href={file.accessUrlLink} target="_blank" rel="noopener noreferrer" download={fileName}>
                Скачать xml форму ЭЗЗ
            </a>
        </div>
    );
};

const formatCargoSize = (cargo) =>
    [
        hasValue(cargo.length) ? `длина ${formatNumber(cargo.length)} м` : null,
        hasValue(cargo.width) ? `ширина ${formatNumber(cargo.width)} м` : null,
        hasValue(cargo.height) ? `высота ${formatNumber(cargo.height)} м` : null,
    ]
        .filter(Boolean)
        .join(", ");

const dateTimeWithTimezoneMask = "DD.MM.YYYY HH:mm (Z)";
const dateTimeWithTimezone = (date) => dateTimeToView(date, dateTimeWithTimezoneMask);

/**
 * @param {{
 * shippingOrder: import("api").ShippingOrderModel,
 * renderActions?: ((shippingOrder: import("api").ShippingOrderModel) => import("react").ReactNode) | null,
 * renderPrintFormAction?: (() => import("react").ReactNode) | null
 * }} props
 */
const ShippingOrderInfo = ({ shippingOrder, renderActions = null, renderPrintFormAction = null }) => {
    if (!shippingOrder) {
        return null;
    }

    const details = shippingOrder.title1Content || shippingOrder.details || {};
    const title2Content = shippingOrder.title2Content;
    const titles = shippingOrder.titles || [];
    const hasT1 = titles.some((t) => t.titleType === shippingOrderTitleTypesEnum.T1);
    const addressPoints = details.addressPoints || [];
    const cargos = details.cargos || [];
    const { vehicle } = details;
    const temperature = [
        hasValue(details.temperatureMinimum) ? `от ${formatNumber(details.temperatureMinimum)}℃` : null,
        hasValue(details.temperatureMaximum) ? `до ${formatNumber(details.temperatureMaximum)}℃` : null,
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <div className="b-etrn">
            <div className="b-etrn__card p-0">
                <div className="show-bid__main-info">
                    <InfoRow label="Статус">
                        <ShippingOrderStatusBadge status={shippingOrder.status} />
                        <ShippingOrderDeclineBadge shippingOrder={shippingOrder} />
                    </InfoRow>
                    {shippingOrder.reason && (
                        <InfoRow label="Причина">
                            <span className="badge badge-danger">
                                {enumLabel(shippingOrderUnmatchingReasons, shippingOrder.reason)}
                            </span>
                        </InfoRow>
                    )}
                    <InfoRow label="Провайдер">
                        {enumLabel(shippingDocumentProviderTypes, shippingOrder.providerType)}
                    </InfoRow>
                    {hasValue(details.orderNumber) && <InfoRow label="Номер ЭЗЗ">{details.orderNumber}</InfoRow>}
                    {hasValue(details.orderDate) && <InfoRow label="Дата ЭЗЗ">{dateToView(details.orderDate)}</InfoRow>}
                    {hasValue(details.gisId) && <InfoRow label="ГИС ID">{details.gisId}</InfoRow>}
                    {hasValue(shippingOrder.externalId) && (
                        <InfoRow label="Внешний ID">{shippingOrder.externalId}</InfoRow>
                    )}
                    {hasValue(shippingOrder.externalSenderId) && (
                        <InfoRow label="ID отправителя">{shippingOrder.externalSenderId}</InfoRow>
                    )}
                    {hasValue(details.otherConditions) && (
                        <InfoRow label="Прочие условия">{details.otherConditions}</InfoRow>
                    )}
                    {temperature && <InfoRow label="Температурный режим">{temperature}</InfoRow>}
                </div>
            </div>

            <ParticipantInfo title="Отправитель" participant={details.shipper} />
            <ParticipantInfo title="Перевозчик" participant={details.carrier} />

            {vehicle && (
                <div className="b-etrn__card p-0">
                    <div className="show-bid__main-info">
                        <div className="font-weight-bold mb-2">Транспорт</div>
                        <div />
                        {hasValue(vehicle.type) && <InfoRow label="Тип">{vehicle.type}</InfoRow>}
                        {hasValue(vehicle.loadCapacity) && (
                            <InfoRow label="Грузоподъемность">{formatNumber(vehicle.loadCapacity)} т</InfoRow>
                        )}
                        {hasValue(vehicle.capacity) && (
                            <InfoRow label="Объем">{formatNumber(vehicle.capacity)} м³</InfoRow>
                        )}
                        {hasValue(vehicle.loadingType) && <InfoRow label="Тип загрузки">{vehicle.loadingType}</InfoRow>}
                        {hasValue(vehicle.bodyType) && <InfoRow label="Тип кузова">{vehicle.bodyType}</InfoRow>}
                        {hasValue(vehicle.otherParams) && <InfoRow label="Параметры">{vehicle.otherParams}</InfoRow>}
                    </div>
                </div>
            )}

            {addressPoints.length > 0 && (
                <div className="b-etrn__card">
                    <div className="font-weight-bold mb-2">Точки</div>
                    {addressPoints.map((point, index) => (
                        <div
                            key={`${point.order}-${index}`}
                            className={index !== addressPoints.length - 1 ? "mb-2 pb-2 border-bottom" : ""}
                        >
                            <div>
                                <b>{enumLabel(shippingOrderOperationTypes, point.operation)}.</b> {point.address}
                            </div>
                            {point.planEnterDateOffset && (
                                <div>
                                    <span className="text-muted">План:</span>{" "}
                                    {dateTimeWithTimezone(point.planEnterDateOffset)}
                                </div>
                            )}
                            {point.secondaryPlanEnterDateOffset && (
                                <div>
                                    <span className="text-muted">Второй план:</span>{" "}
                                    {dateTimeWithTimezone(point.secondaryPlanEnterDateOffset)}
                                </div>
                            )}
                            {(point.counterpartyName || point.counterpartyInn) && (
                                <div>
                                    <span className="text-muted">Контрагент:</span>{" "}
                                    {[point.counterpartyName, point.counterpartyInn].filter(Boolean).join(", ")}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {cargos.length > 0 && (
                <div className="b-etrn__card">
                    <div className="font-weight-bold mb-2">Груз</div>
                    {cargos.map((cargo, index) => {
                        const cargoSize = formatCargoSize(cargo);
                        return (
                            <div
                                key={`${cargo.name}-${index}`}
                                className={index !== cargos.length - 1 ? "mb-2 pb-2 border-bottom" : ""}
                            >
                                {cargo.name && <div>{cargo.name}</div>}
                                {hasValue(cargo.packType) && (
                                    <div>
                                        <span className="text-muted">Вид тары:</span> {cargo.packType}
                                    </div>
                                )}
                                {hasValue(cargo.placesCount) && (
                                    <div>
                                        <span className="text-muted">Количество мест:</span> {cargo.placesCount}
                                    </div>
                                )}
                                {hasValue(cargo.weight) && (
                                    <div>
                                        <span className="text-muted">Вес:</span> {formatNumber(cargo.weight)} кг
                                    </div>
                                )}
                                {hasValue(cargo.volume) && (
                                    <div>
                                        <span className="text-muted">Объем:</span> {formatNumber(cargo.volume)} м³
                                    </div>
                                )}
                                {cargoSize.length > 0 && (
                                    <div>
                                        <span className="text-muted">Габариты:</span> {cargoSize}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {title2Content && (
                <div className="b-etrn__card p-0">
                    <div className="show-bid__main-info">
                        <div className="font-weight-bold mb-2">Содержание Т2</div>
                        <div />
                        {hasValue(title2Content.documentCreator) && (
                            <InfoRow label="Создатель документа">{title2Content.documentCreator}</InfoRow>
                        )}
                        {hasValue(title2Content.senderFnsParticipantId) && (
                            <InfoRow label="ID отправителя ФНС">{title2Content.senderFnsParticipantId}</InfoRow>
                        )}
                        {hasValue(title2Content.recipientFnsParticipantId) && (
                            <InfoRow label="ID получателя ФНС">{title2Content.recipientFnsParticipantId}</InfoRow>
                        )}
                        {hasValue(title2Content.receivingOperation) && (
                            <InfoRow label="Результат рассмотрения">{title2Content.receivingOperation}</InfoRow>
                        )}
                        {hasValue(title2Content.rejectionReason) && (
                            <InfoRow label="Причина отказа">{title2Content.rejectionReason}</InfoRow>
                        )}
                        {hasValue(title2Content.organizerCarrierName) && (
                            <InfoRow label="Перевозчик">{title2Content.organizerCarrierName}</InfoRow>
                        )}
                        {title2Content.organizerCarrierPhones?.length > 0 && (
                            <InfoRow label="Телефоны перевозчика">
                                {title2Content.organizerCarrierPhones.join(", ")}
                            </InfoRow>
                        )}
                    </div>
                    {title2Content.drivers?.length > 0 && (
                        <div className="border-top p-3">
                            <div className="font-weight-bold mb-2">Водители</div>
                            {title2Content.drivers.map((driver, index) => (
                                <div
                                    key={`${driver.inn || driver.fullName}-${index}`}
                                    className={
                                        index < title2Content.drivers.length - 1 ? "border-bottom mb-2 pb-2" : ""
                                    }
                                >
                                    <div>{driver.fullName || "Водитель"}</div>
                                    {driver.inn && <div className="text-muted small">ИНН {driver.inn}</div>}
                                    {(driver.licenseSeries || driver.licenseNumber) && (
                                        <div className="text-muted small">
                                            ВУ {[driver.licenseSeries, driver.licenseNumber].filter(Boolean).join(" ")}
                                            {driver.licenseDate ? ` от ${dateToView(driver.licenseDate)}` : ""}
                                        </div>
                                    )}
                                    {driver.phones?.length > 0 && (
                                        <div className="text-muted small">{driver.phones.join(", ")}</div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    {(title2Content.vehicle || title2Content.trailers?.length > 0) && (
                        <div className="border-top p-3">
                            <div className="font-weight-bold mb-2">Транспорт</div>
                            {[title2Content.vehicle, ...(title2Content.trailers || [])]
                                .filter(Boolean)
                                .map((vehicle, index) => (
                                    <div
                                        key={`${vehicle.registrationNumber || vehicle.vin}-${index}`}
                                        className={index > 0 ? "border-top mt-2 pt-2" : ""}
                                    >
                                        <div>
                                            {[vehicle.brand, vehicle.type, vehicle.registrationNumber]
                                                .filter(Boolean)
                                                .join(", ") || "Транспортное средство"}
                                        </div>
                                        {vehicle.vin && <div className="text-muted small">VIN {vehicle.vin}</div>}
                                        {(hasValue(vehicle.weightCapacity) || hasValue(vehicle.volumeCapacity)) && (
                                            <div className="text-muted small">
                                                {[
                                                    hasValue(vehicle.weightCapacity)
                                                        ? `${formatNumber(vehicle.weightCapacity)} т`
                                                        : null,
                                                    hasValue(vehicle.volumeCapacity)
                                                        ? `${formatNumber(vehicle.volumeCapacity)} м³`
                                                        : null,
                                                ]
                                                    .filter(Boolean)
                                                    .join(", ")}
                                            </div>
                                        )}
                                    </div>
                                ))}
                        </div>
                    )}
                    {title2Content.transportFee && (
                        <div className="border-top p-3">
                            <div className="font-weight-bold mb-2">Стоимость перевозки</div>
                            <div>
                                {hasValue(title2Content.transportFee.subtotal)
                                    ? formatNumber(title2Content.transportFee.subtotal)
                                    : "—"}{" "}
                                {title2Content.transportFee.currencyName ||
                                    title2Content.transportFee.currencyCode ||
                                    ""}
                            </div>
                            {title2Content.transportFee.taxRate && (
                                <div className="text-muted small">
                                    Налоговая ставка: {title2Content.transportFee.taxRate}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            <div className="b-etrn__card">
                <div className="d-flex justify-content-between align-items-baseline mb-2">
                    <div className="font-weight-bold">Титулы</div>
                    {renderActions?.(shippingOrder)}
                </div>
                {titles.length > 0 ? (
                    titles.map((title, index) => {
                        const isT1OrFirstFallback =
                            title.titleType === shippingOrderTitleTypesEnum.T1 || (!hasT1 && index === 0);

                        return (
                            <div
                                key={title.id}
                                className={
                                    index !== titles.length - 1 ? "mb-2 pb-2 border-bottom border-secondary" : ""
                                }
                            >
                                <div className="d-flex justify-content-between align-items-baseline">
                                    <b>
                                        {title.titleType === shippingOrderTitleTypesEnum.T2 &&
                                        isShippingOrderDeclined(shippingOrder)
                                            ? "Т2 - отказ в согласовании заказ-заявки"
                                            : enumLabel(shippingOrderTitleTypes, title.titleType)}
                                        .
                                    </b>
                                    <span className={titleStatusClassName(title.status)}>
                                        {enumLabel(shippingOrderTitleStatuses, title.status)}
                                    </span>
                                </div>
                                {title.createDate && (
                                    <div>
                                        <span className="text-muted">Создан:</span>{" "}
                                        {dateTimeWithTimezone(title.createDate)}
                                    </div>
                                )}
                                {title.externalId && (
                                    <div>
                                        <span className="text-muted">Внешний ID:</span> {title.externalId}
                                    </div>
                                )}
                                {title.signatureExternalId && (
                                    <div>
                                        <span className="text-muted">ID подписи:</span> {title.signatureExternalId}
                                    </div>
                                )}
                                <FileLink file={title.titleFile} />
                                {renderPrintFormAction && isT1OrFirstFallback && (
                                    <div className="mt-1">{renderPrintFormAction()}</div>
                                )}
                            </div>
                        );
                    })
                ) : (
                    <div>
                        <span className="text-muted">Титулы не найдены</span>
                        {renderPrintFormAction && <div className="mt-2">{renderPrintFormAction()}</div>}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ShippingOrderInfo;
